import { IconButton, LoadingSpinner, Switch, Tabs } from '@neo4j-ndl/react';
import { XMarkIconOutline } from '@neo4j-ndl/react/icons';
import { OPS_EVENTS } from '@nx/analytics-service';
import type { Instance, TimeRange } from '@nx/state';
import { TIER, consoleApi, useActiveProject } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { Center, Page } from '@nx/ui';
import type { EntityOption } from '@nx/ui/src/entity-selector-dropdown/entity-selector-dropdown';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { DatabaseInstance } from '../../../../packages/state/src/slices/fleet-management/types';
import { MAX_DATABASES_FOR_METRICS } from '../fleet-management/shared/constants';
import { track } from '../services/segment/analytics';
import { ApiErrorBanner } from '../shared/components';
import { DatabaseSelector } from '../shared/components/database-selector';
import { InstanceBar } from '../shared/components/instance-bar';
import { InstanceSelector } from '../shared/components/instance-selector';
import { withUpxContext } from '../shared/components/upx-context';
import { useInstanceSelectorProps } from '../shared/hooks/use-instance-selector-props';
import { useIsScrolled } from '../shared/hooks/use-is-scrolled';
import type { ChartType } from './charts/fullstack-chart-props';
import { chartKey, useMetricsViewState } from './hooks';
import MetricsView, {
  MetricsNotSupportedView,
  NoInstancesView,
  UpgradeDeploymentView,
  UpgradeMetricsView,
} from './metrics-view';
import classes from './metrics.module.css';
import { AuraMetricSource } from './shared/aura-metric-source';
import { TimePeriodAndRefresh } from './shared/components/time-window-selector';
import { FleetDbmsMetricSource } from './shared/fleet-dbms-metric-source';
import type { DropdownOptions } from './shared/types';

const AuraViewSwitchOptions: DropdownOptions<ChartType> = [
  {
    value: 'HOST',
    label: 'Resources',
  },
  {
    value: 'INSTANCE',
    label: 'Instance',
  },
  {
    value: 'DB',
    label: 'Database',
  },
];

const DEFAULT_TAB: ChartType = 'HOST';

const MetricsPage = () => {
  const activeProject = useActiveProject();
  const fleetManagementEnabled = activeProject.capabilities.fleet_management;
  const {
    selectedInstanceSummary,
    selectedInstance,
    setSelectedInstanceId,
    instanceSummaries,
    isInstancesLoading,
    instances,
    selectedDeploymentSummary,
    selectedDeployment,
    deployments,
    isDeploymentsLoading,
  } = useInstanceSelectorProps({ dualMode: fleetManagementEnabled });

  const { state, chartContext, setTimePeriod, zoomIn, refreshTimeRange, setExpandedChart } = useMetricsViewState({});
  const [useAZGrouping, setUseAZGrouping] = useState<boolean>(false);
  const [showLeaderOnly, setShowLeaderOnly] = useState<boolean>(false);

  const isDetailedMetricsAvailable = (instance: Instance | undefined) =>
    isNotNullish(instance) && (instance.tier === TIER.ENTERPRISE || instance.tier === TIER.MTE);

  useEffect(() => {
    if (!isDetailedMetricsAvailable(selectedInstance)) {
      setUseAZGrouping(false);
      setShowLeaderOnly(false);
    }
  }, [selectedInstance]);

  const [databaseOptions, setDatabaseOptions] = useState<EntityOption[]>([]);
  const [databaseNamesError, setDatabaseNamesError] = useState<string | undefined>(undefined);
  const [getDatabaseNames] = consoleApi.useLazyListDatabasesQuery();

  useEffect(() => {
    const fetchDatabaseNames = async () => {
      if (!selectedInstance) {
        return;
      }
      const { data: databaseNamesResult, error } = await getDatabaseNames(selectedInstance.id);
      if (isNotNullish(error)) {
        setDatabaseNamesError(`Unable to fetch database names: ${JSON.stringify(error)}`);
      } else if (isNullish(databaseNamesResult) || databaseNamesResult.length === 0) {
        setDatabaseNamesError('No databases found');
      } else {
        setDatabaseOptions(
          databaseNamesResult.map((db) => ({
            id: db.name,
            name: db.name,
          })),
        );
      }
    };
    if (isNotNullish(selectedInstance) && selectedInstance.capabilities.multi_database.enabled) {
      void fetchDatabaseNames();
    } else if (isNotNullish(selectedDeployment)) {
      // TODO refactor: move database name retrieval and disabled reason calculation to the metric source classes
      const getDatabaseInstance = (databaseName: string) =>
        selectedDeployment.dbms?.servers
          .flatMap((server) => server.databaseInstances)
          .find((dbi) => dbi.databaseName === databaseName);
      const getDisabledReason = (databaseInstance: DatabaseInstance | undefined) => {
        if (isNotNullish(databaseInstance) && databaseInstance.type === 'composite') {
          return 'No metrics available for composite databases';
        }
        if (
          isNotNullish(databaseInstance) &&
          isNotNullish(databaseInstance.propertyShards) &&
          databaseInstance.propertyShards.length > 0
        ) {
          return 'No metrics available for top-level sharded databases';
        }
        return undefined;
      };
      setDatabaseOptions(
        selectedDeployment.dbms?.databases.map((db) => {
          const databaseInstance = getDatabaseInstance(db.name);
          const disabledReason = getDisabledReason(databaseInstance);
          return {
            id: db.name,
            name: db.name,
            disabled: isNotNullish(disabledReason),
            disabledReason,
          };
        }) ?? [],
      );
    } else if (isNotNullish(selectedInstance)) {
      setDatabaseOptions([{ id: 'neo4j', name: 'neo4j' }]);
    }
  }, [getDatabaseNames, selectedDeployment, selectedInstance]);

  const [view, setView] = useState<ChartType>(DEFAULT_TAB);

  const [currentDbName, setCurrentDbName] = useState<string | undefined>(databaseOptions[0]?.name);

  useEffect(() => {
    // Reset currentDbName if the selected dbms does not have the current db name
    if (selectedDeployment && !selectedDeployment.dbms?.databases.find((db) => db.name === currentDbName)) {
      setCurrentDbName(databaseOptions[0]?.name);
    }
    if (selectedInstanceSummary) {
      setCurrentDbName(databaseOptions[0]?.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [databaseOptions]);

  const trackMetrics = useCallback<typeof track>(
    (event, props) => {
      return track(event, {
        tab: view,
        expandedChart: state.expandedChart && chartKey(state.expandedChart),
        ...props,
      });
    },
    [state.expandedChart, view],
  );
  const selectInstance = useCallback(
    (instanceid: string | undefined) => {
      setCurrentDbName(undefined);
      setSelectedInstanceId(instanceid);
    },
    [setSelectedInstanceId],
  );

  const setTimePeriodTracked = useCallback<typeof setTimePeriod>(
    (timePeriod, customTimeRange) => {
      setTimePeriod(timePeriod, customTimeRange);
      trackMetrics(OPS_EVENTS.METRICS_CHANGE_TIME_PERIOD, {
        timePeriod,
      });
    },
    [setTimePeriod, trackMetrics],
  );

  const onZoom = useCallback(
    (timeRange: TimeRange | null = null) => {
      zoomIn(timeRange);
    },
    [zoomIn],
  );

  const onZoomReset = useCallback(() => {
    if (state.timeRangeZoomedIn) {
      //  zooms out chart instantly since state.timeRangeDerived metrics are already in cache
      zoomIn(null);
      //  schedules a refresh to update state.timeRangeDerived and get latest metrics
      setTimeout(() => refreshTimeRange(), 500);
    }
  }, [refreshTimeRange, state.timeRangeZoomedIn, zoomIn]);

  const onRefreshTracked = useCallback(() => {
    refreshTimeRange();
    trackMetrics(OPS_EVENTS.METRICS_REFRESH);
  }, [refreshTimeRange, trackMetrics]);

  const timePeriodAndRefresh = useMemo(
    () => (
      <TimePeriodAndRefresh
        key="time-period-and-refresh"
        timeRange={chartContext.timeRange}
        timePeriod={state.timePeriod}
        onTimePeriodChange={setTimePeriodTracked}
        onRefresh={onRefreshTracked}
      />
    ),
    [chartContext.timeRange, onRefreshTracked, setTimePeriodTracked, state.timePeriod],
  );

  const expandedChartProps = useMemo(
    () => ({
      isExpanded: false,
      onSetExpanded: setExpandedChart,
      expandedControls: {
        timePeriodAndRefresh,
        close: (
          <IconButton
            //  TODO: add aria-label
            ariaLabel=""
            isClean
            onClick={() => setExpandedChart(null)}
            key="close-expanded-button"
          >
            <XMarkIconOutline />
          </IconButton>
        ),
      },
    }),
    [setExpandedChart, timePeriodAndRefresh],
  );

  const selectedMetricSource = useMemo(() => {
    if (isNotNullish(selectedInstance)) {
      const isDbmsEnterprise = selectedInstance.tier === TIER.ENTERPRISE || selectedInstance.tier === TIER.MTE;
      return new AuraMetricSource(
        isDbmsEnterprise && !useAZGrouping,
        useAZGrouping,
        showLeaderOnly,
        selectedInstance,
        currentDbName,
      );
    } else if (isNotNullish(selectedDeployment)) {
      return new FleetDbmsMetricSource(activeProject.id, selectedDeployment, true, currentDbName);
    }
    return undefined;
  }, [selectedInstance, selectedDeployment, useAZGrouping, showLeaderOnly, currentDbName, activeProject.id]);

  const metricsRootElRef = useRef<HTMLDivElement | null>(null);
  const { isScrolled } = useIsScrolled(metricsRootElRef.current?.parentElement);
  const getTabView = useMemo(() => {
    if (
      !(selectedInstance ?? selectedDeployment) ||
      (view === 'DB' &&
        databaseOptions.find((db) => db.name === currentDbName) === undefined &&
        isNullish(databaseNamesError))
    ) {
      return (
        <Center className="mt-[10%]">
          <LoadingSpinner size="large" />
        </Center>
      );
    } else if (isNotNullish(databaseNamesError) && view === 'DB') {
      return <ApiErrorBanner error={{ message: databaseNamesError }} description={databaseNamesError} />;
    } else if (selectedInstance && selectedInstance.tier === TIER.FREE) {
      return <UpgradeMetricsView instance={selectedInstance} viewType={view} chartContext={chartContext} />;
    } else if (selectedDeployment && selectedDeployment.dbms?.edition === 'community') {
      return <UpgradeDeploymentView deployment={selectedDeployment} viewType={view} chartContext={chartContext} />;
    } else if (
      selectedDeployment &&
      isNotNullish(selectedDeployment.dbms?.databases) &&
      selectedDeployment.dbms.databases.length > MAX_DATABASES_FOR_METRICS
    ) {
      return <MetricsNotSupportedView deployment={selectedDeployment} viewType={view} chartContext={chartContext} />;
    } else if (isNotNullish(selectedInstance) || isNotNullish(selectedDeployment)) {
      return (
        <MetricsView
          metricSource={selectedMetricSource}
          viewType={view}
          chartContext={chartContext}
          viewState={state}
          expandProps={expandedChartProps}
          onZoom={onZoom}
          onZoomReset={onZoomReset}
        />
      );
    }
    return null;
  }, [
    chartContext,
    currentDbName,
    databaseOptions,
    databaseNamesError,
    expandedChartProps,
    onZoom,
    onZoomReset,
    selectedDeployment,
    selectedMetricSource,
    selectedInstance,
    state,
    view,
  ]);

  if (isInstancesLoading || isDeploymentsLoading) {
    return (
      <Center>
        <LoadingSpinner size="large" />
      </Center>
    );
  }

  if (instanceSummaries.length === 0 && deployments.length === 0) {
    return <NoInstancesView viewType={view} chartContext={chartContext} />;
  }

  return (
    <div ref={metricsRootElRef}>
      <div className="border-palette-neutral-border-weak bg-palette-neutral-bg-weak sticky top-0 z-10">
        <div className="border-palette-neutral-border-weak bg-palette-neutral-bg-weak flex min-h-[56px] flex-wrap-reverse items-center justify-between border-b-[1px] px-4">
          <div className="flex min-h-[55px] flex-wrap gap-4">
            <Tabs
              fill="underline"
              onChange={(newView) => {
                track(OPS_EVENTS.METRICS_VIEW, { view: newView });
                zoomIn(null);
                setView(newView);
              }}
              value={view}
              className={classNames(classes.tabs, 'self-end')}
            >
              {AuraViewSwitchOptions.map((option) => (
                <Tabs.Tab
                  tabId={option.value}
                  key={option.value}
                  htmlAttributes={{
                    'data-testid': option.value,
                  }}
                >
                  {option.label}
                </Tabs.Tab>
              ))}
            </Tabs>
          </div>
          <div className="flex min-h-[55px] flex-wrap items-center gap-2">{timePeriodAndRefresh}</div>
        </div>
        <InstanceBar shadow={isScrolled}>
          <div className="flex flex-wrap gap-2">
            <InstanceSelector
              selectedInstanceSummary={selectedInstanceSummary}
              setSelectedInstanceId={selectInstance}
              instances={instances}
              instanceSummaries={instanceSummaries}
              deployments={deployments}
              selectedDeploymentSummary={selectedDeploymentSummary}
              dualMode={fleetManagementEnabled}
            />
            {view === 'DB' &&
              (databaseOptions.length > 1 ||
                (isNotNullish(selectedInstance) && selectedInstance.capabilities.multi_database.enabled)) && (
                <DatabaseSelector
                  selectedDatabase={currentDbName ?? ''}
                  setSelectedDatabase={setCurrentDbName}
                  databases={databaseOptions}
                />
              )}
          </div>
          {selectedInstance && isDetailedMetricsAvailable(selectedInstance) && (
            <div className="flex flex-wrap items-center gap-4">
              {view === 'DB' && useAZGrouping && (
                <Switch
                  htmlAttributes={{
                    'aria-label': `${showLeaderOnly ? 'Disable' : 'Enable'} leader-only view`,
                  }}
                  isChecked={showLeaderOnly}
                  onChange={() => setShowLeaderOnly(!showLeaderOnly)}
                  label="Leader only"
                />
              )}
              <Switch
                onChange={() => setUseAZGrouping(!useAZGrouping)}
                label="Detail view"
                isChecked={useAZGrouping}
                htmlAttributes={{
                  'aria-label': `${useAZGrouping ? 'Disable' : 'Enable'} detail view`,
                }}
              />
            </div>
          )}
        </InstanceBar>
      </div>

      <Page contentType="heavy">
        <Tabs.TabPanel tabId={view} value={view} className="m-0">
          {getTabView}
        </Tabs.TabPanel>
      </Page>
    </div>
  );
};

const MetricsPageWithUpxContext = withUpxContext(MetricsPage);

export default MetricsPageWithUpxContext;
